import { isAxiosError } from "axios";
import { z } from "zod";

/** One refused broadcast from an all-or-nothing schedule 400 (CON-310). */
export interface ScheduleRefusal {
  broadcastId: string;
  reason: string;
}

/** gRPC-gateway body of a FailedPrecondition: details carry one
 * BEAT_NOT_READY violation per refused broadcast (subject = broadcast id,
 * description = reason). Other detail/violation kinds are ignored. */
const refusalBodySchema = z.object({
  details: z.array(
    z.object({
      violations: z
        .array(
          z.object({
            type: z.string(),
            subject: z.string().optional(),
            description: z.string().optional(),
          }),
        )
        .optional(),
    }),
  ),
});

/** Refused broadcasts from a campaigns:schedule (or create-as-scheduled)
 * error. Empty means "not a schedule refusal": fall back to generic copy. */
export function scheduleRefusals(error: unknown): ScheduleRefusal[] {
  if (!isAxiosError(error)) return [];
  const parsed = refusalBodySchema.safeParse(error.response?.data);
  if (!parsed.success) return [];
  return parsed.data.details
    .flatMap((detail) => detail.violations ?? [])
    .filter((violation) => violation.type === "BEAT_NOT_READY")
    .map((violation) => ({
      broadcastId: violation.subject ?? "",
      reason: violation.description ?? "the backend refused it",
    }));
}

/** Multiline snackbar copy: the lead states the outcome ("The campaign stays
 * a draft"), then each refused send gets its own line. Names resolve from the
 * given broadcasts when known. */
export function scheduleRefusalMessage(
  lead: string,
  refusals: ScheduleRefusal[],
  broadcasts: { broadcast_id: string; name: string }[] = [],
): string {
  const lines = refusals.map((refusal) => {
    const name = broadcasts.find(
      (broadcast) => broadcast.broadcast_id === refusal.broadcastId,
    )?.name;
    return `• ${name ?? "A send"}: ${refusal.reason}`;
  });
  const count =
    refusals.length === 1
      ? "a send was refused"
      : `${refusals.length} sends were refused`;
  return `${lead}, ${count}:\n${lines.join("\n")}`;
}
